import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { carpData, strategyStatuses, STATUS_IMAGE_MAP } from '../data/carpData';
import './Carp2026.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

const PRIORITIES = [1, 2, 3];
const COSTS = ['$', '$$', '$$$'];
const COBENEFITS = [
  { value: 'Equity', label: 'Equity' },
  { value: 'Economic', label: 'Economic' },
  { value: 'Public Health', label: 'Public Health' },
  { value: 'Infrastructure Resilience', label: 'Resilience' },
];
const CARP_STATUSES = ['Pre-Planning', 'Planning', 'In Development', 'Implementation', 'Enacted'];

function getAllStrategies(data) {
  const strategies = [];
  data.categories.forEach(cat => {
    cat.goals.forEach(goal => {
      goal.strategies.forEach(strategy => {
        strategies.push(strategy);
      });
    });
  });
  if (data.crossCuttingStrategy) {
    strategies.push(data.crossCuttingStrategy);
  }
  return strategies;
}

function Carp2026() {
  const [sel, setSel] = useState({ priority: new Set(), cost: new Set(), coBenefit: new Set(), status: new Set() });
  const [expandedCategories, setExpandedCategories] = usePersistentSet('carp-2026:expandedCategories', () => new Set(carpData.categories.map(c => c.id)));
  const [expandedGoals, setExpandedGoals] = usePersistentSet('carp-2026:expandedGoals');
  const [expandedStrategies, setExpandedStrategies] = usePersistentSet('carp-2026:expandedStrategies');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('carp-2026:overviewExpanded');
  const [equityExpanded, setEquityExpanded] = usePersistentFlag('carp-2026:equityExpanded');
  const [frameworkExpanded, setFrameworkExpanded] = usePersistentFlag('carp-2026:frameworkExpanded');
  const [crossCuttingExpanded, setCrossCuttingExpanded] = usePersistentFlag('carp-2026:crossCuttingExpanded', true);

  useEffect(() => {
    document.title = 'CARP 2026 - Climate Action and Resiliency Plan';
  }, []);

  const allStrategies = useMemo(() => getAllStrategies(carpData), []);

  const dimensions = useMemo(() => [
    { key: 'priority', label: 'Priority', values: PRIORITIES.map(v => ({
      value: v, label: `Priority ${v}`, count: allStrategies.filter(s => s.priorityScore === v).length,
    })) },
    { key: 'cost', label: 'Cost', values: COSTS.map(v => ({
      value: v, label: v, count: allStrategies.filter(s => s.costEstimate === v).length,
    })) },
    { key: 'coBenefit', label: 'Co-Benefits', values: COBENEFITS.map(cb => ({
      value: cb.value, label: cb.label, count: allStrategies.filter(s => s.coBenefits && s.coBenefits.includes(cb.value)).length,
    })) },
    { key: 'status', label: 'Status', values: CARP_STATUSES.map(v => ({
      value: v, label: v, count: allStrategies.filter(s => strategyStatuses[s.id] === v).length,
    })) },
  ], [allStrategies]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ priority: new Set(), cost: new Set(), coBenefit: new Set(), status: new Set() });

  const matchesStrategy = useMemo(() => {
    return (strategy) => {
      if (sel.priority.size && !sel.priority.has(strategy.priorityScore)) return false;
      if (sel.cost.size && !sel.cost.has(strategy.costEstimate)) return false;
      if (sel.coBenefit.size && !(strategy.coBenefits && [...sel.coBenefit].some(cb => strategy.coBenefits.includes(cb)))) return false;
      if (sel.status.size && !sel.status.has(strategyStatuses[strategy.id])) return false;
      return true;
    };
  }, [sel]);

  const filteredCategories = useMemo(() => {
    return carpData.categories.map(category => ({
      ...category,
      goals: category.goals.map(goal => ({
        ...goal,
        strategies: goal.strategies.filter(matchesStrategy)
      })).filter(goal => goal.strategies.length > 0)
    })).filter(category => category.goals.length > 0);
  }, [matchesStrategy]);

  const crossCuttingVisible = useMemo(() => {
    if (!carpData.crossCuttingStrategy) return false;
    return matchesStrategy(carpData.crossCuttingStrategy);
  }, [matchesStrategy]);

  const totalFilteredStrategies = useMemo(() => {
    let count = filteredCategories.reduce((sum, cat) =>
      sum + cat.goals.reduce((s2, g) => s2 + g.strategies.length, 0), 0);
    if (crossCuttingVisible) count += 1;
    return count;
  }, [filteredCategories, crossCuttingVisible]);


  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const toggleGoal = (goalId) => {
    setExpandedGoals(prev => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }
      return next;
    });
  };

  const toggleStrategy = (strategyId) => {
    setExpandedStrategies(prev => {
      const next = new Set(prev);
      if (next.has(strategyId)) next.delete(strategyId);
      else next.add(strategyId);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set(carpData.categories.map(c => c.id)));
    const allGoalIds = new Set();
    const allStrategyIds = new Set();
    carpData.categories.forEach(cat => {
      cat.goals.forEach(goal => {
        allGoalIds.add(goal.id);
        goal.strategies.forEach(s => allStrategyIds.add(s.id));
      });
    });
    setExpandedGoals(allGoalIds);
    setExpandedStrategies(allStrategyIds);
    setCrossCuttingExpanded(true);
    setOverviewExpanded(true);
    setEquityExpanded(true);
    setFrameworkExpanded(true);
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setExpandedGoals(new Set());
    setExpandedStrategies(new Set());
    setCrossCuttingExpanded(false);
    setOverviewExpanded(false);
    setEquityExpanded(false);
    setFrameworkExpanded(false);
  };

  const priorityLabel = (score) => {
    if (score === 1) return 'P1';
    if (score === 2) return 'P2';
    if (score === 3) return 'P3';
    return '';
  };

  const priorityClass = (score) => {
    if (score === 1) return 'carp-priority-1';
    if (score === 2) return 'carp-priority-2';
    if (score === 3) return 'carp-priority-3';
    return '';
  };

  const costLabel = (cost) => cost || '';
  const staffLabel = (staff) => {
    if (staff === 1) return '1 staff';
    if (staff === 2) return '2 staff';
    if (staff === 3) return '3+ staff';
    return '';
  };

  const renderStrategyMetadata = (strategy) => (
    <div className="carp-strategy-metadata">
      <div className="carp-meta-row">
        <span className={`carp-priority-badge ${priorityClass(strategy.priorityScore)}`}>
          {priorityLabel(strategy.priorityScore)}
        </span>
        {strategy.ghgRole && (
          <span className="carp-meta-item">
            <span className="carp-meta-label">GHG Role:</span> {strategy.ghgRole}
          </span>
        )}
        {strategy.startBy && (
          <span className="carp-meta-item">
            <span className="carp-meta-label">Start By:</span> {strategy.startBy}
          </span>
        )}
        {strategy.costEstimate && (
          <span className="carp-meta-item">
            <span className="carp-meta-label">Cost:</span> {costLabel(strategy.costEstimate)}
          </span>
        )}
        {strategy.staffEstimate && (
          <span className="carp-meta-item">
            <span className="carp-meta-label">Staff:</span> {staffLabel(strategy.staffEstimate)}
          </span>
        )}
      </div>
      {strategy.coBenefits && strategy.coBenefits.length > 0 && (
        <div className="carp-cobenefits">
          <span className="carp-meta-label">Co-Benefits:</span>
          {strategy.coBenefits.map((cb, i) => (
            <span key={i} className="carp-cobenefit-pill">{cb}</span>
          ))}
        </div>
      )}
      {strategy.partneringDept && (
        <div className="carp-partnering-dept">
          Partnering Department: {strategy.partneringDept}
        </div>
      )}
    </div>
  );

  const renderActions = (actions) => (
    <div className="carp-actions">
      {actions.map(action => (
        <div key={action.id} className="carp-action">
          <span className="carp-action-id">{action.id}.</span>
          <span className="carp-action-desc">{action.description}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="carp-page">
      <a href="#main-content" className="carp-skip-link">Skip to main content</a>
      <Header
        title="Brookline CARP 2026"
        subtitle="Climate Action and Resiliency Plan"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="carp-content">
        <div className="carp-intro">
          <p className="carp-updated">Updated {carpData.updatedDate}</p>
        </div>

        <div className="carp-controls">
          <FilterBar
            allCount={allStrategies.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="carp-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="carp-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFilteredStrategies}
          noun="strategy"
          nounPlural="strategies"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="carp-preamble-sections">
          <div className="carp-preamble">
            <button
              className={`carp-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`carp-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="carp-preamble-title">Overview</span>
            </button>
            {overviewExpanded && (
              <div className="carp-preamble-body">
                {carpData.introduction.overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="carp-preamble">
            <button
              className={`carp-preamble-header ${equityExpanded ? 'expanded' : ''}`}
              onClick={() => setEquityExpanded(!equityExpanded)}
              aria-expanded={equityExpanded}
            >
              <span className={`carp-chevron${equityExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="carp-preamble-title">Equity & Environmental Justice</span>
            </button>
            {equityExpanded && (
              <div className="carp-preamble-body">
                {carpData.introduction.equity.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="carp-preamble">
            <button
              className={`carp-preamble-header ${frameworkExpanded ? 'expanded' : ''}`}
              onClick={() => setFrameworkExpanded(!frameworkExpanded)}
              aria-expanded={frameworkExpanded}
            >
              <span className={`carp-chevron${frameworkExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="carp-preamble-title">Plan Framework</span>
            </button>
            {frameworkExpanded && (
              <div className="carp-preamble-body">
                {carpData.introduction.framework.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>
        </div>

        {crossCuttingVisible && carpData.crossCuttingStrategy && (
          <div className="carp-cross-cutting">
            <button
              className={`carp-cross-cutting-header ${crossCuttingExpanded ? 'expanded' : ''}`}
              onClick={() => setCrossCuttingExpanded(!crossCuttingExpanded)}
              aria-expanded={crossCuttingExpanded}
            >
              <span className={`carp-chevron${crossCuttingExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="carp-cross-cutting-label">Cross-Cutting Strategy</span>
              <span className="carp-cross-cutting-title">
                {carpData.crossCuttingStrategy.id}: {carpData.crossCuttingStrategy.title}
              </span>
              <span className={`carp-priority-badge ${priorityClass(carpData.crossCuttingStrategy.priorityScore)}`}>
                {priorityLabel(carpData.crossCuttingStrategy.priorityScore)}
              </span>
            </button>
            {crossCuttingExpanded && (
              <div className="carp-cross-cutting-body">
                <p className="carp-cross-cutting-desc">{carpData.crossCuttingStrategy.description}</p>
                {renderStrategyMetadata(carpData.crossCuttingStrategy)}
                {carpData.crossCuttingStrategy.actions && carpData.crossCuttingStrategy.actions.length > 0 && (
                  renderActions(carpData.crossCuttingStrategy.actions)
                )}
              </div>
            )}
          </div>
        )}

        <div className="carp-categories">
          {filteredCategories.map(category => (
            <div key={category.id} className="carp-category">
              <h2 className="carp-heading"><button
                className={`carp-category-header ${expandedCategories.has(category.id) ? 'expanded' : ''}`}
                onClick={() => toggleCategory(category.id)}
                aria-expanded={expandedCategories.has(category.id)}
              >
                <span className="carp-cathead-focus">
                  <span className={`carp-chevron carp-chevron-light${expandedCategories.has(category.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                  <div className="carp-category-header-text">
                    <span className="carp-category-name">{category.name}</span>
                    {category.description && (
                      <span className="carp-category-desc">{category.description}</span>
                    )}
                  </div>
                </span>
              </button></h2>

              {expandedCategories.has(category.id) && (
                <div className="carp-category-body">
                  {category.goals.map(goal => (
                    <div key={goal.id} className="carp-goal">
                      <h3 className="carp-heading"><button
                        className={`carp-goal-header ${expandedGoals.has(goal.id) ? 'expanded' : ''}`}
                        onClick={() => toggleGoal(goal.id)}
                        aria-expanded={expandedGoals.has(goal.id)}
                      >
                        <span className={`carp-chevron${expandedGoals.has(goal.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                        <span className="carp-goal-id">Goal {goal.id}:</span>
                        <span className="carp-goal-title">{goal.title}</span>
                      </button></h3>

                      {expandedGoals.has(goal.id) && (
                        <div className="carp-goal-body">
                          {goal.climateImpact && (
                            <p className="carp-climate-impact">
                              <span className="carp-climate-impact-label">Climate Impact: </span>
                              {goal.climateImpact}
                            </p>
                          )}

                          {goal.strategies.map(strategy => {
                            const isExpanded = expandedStrategies.has(strategy.id);
                            return (
                              <div key={strategy.id} className="carp-strategy">
                                <h4 className="carp-heading"><button
                                  className={`carp-strategy-header ${isExpanded ? 'expanded' : ''}`}
                                  onClick={() => toggleStrategy(strategy.id)}
                                  aria-expanded={isExpanded}
                                >
                                  <span className={`carp-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                                  <span className="carp-strategy-id">{strategy.id}.</span>
                                  <span className="carp-strategy-title">{strategy.title}</span>
                                  <span className={`carp-priority-badge small ${priorityClass(strategy.priorityScore)}`}>
                                    {priorityLabel(strategy.priorityScore)}
                                  </span>
                                  {strategyStatuses[strategy.id] && (
                                    <span className="carp-status-indicator">
                                      <img
                                        src={STATUS_IMAGE_MAP[strategyStatuses[strategy.id]]}
                                        alt=""
                                        className="carp-status-img"
                                      />
                                      <span className="carp-status-label">{strategyStatuses[strategy.id]}</span>
                                    </span>
                                  )}
                                  <span className="carp-action-count">
                                    {strategy.actions.length} action{strategy.actions.length !== 1 ? 's' : ''}
                                  </span>
                                </button></h4>

                                {isExpanded && (
                                  <div className="carp-strategy-body">
                                    {renderStrategyMetadata(strategy)}
                                    {strategy.actions && strategy.actions.length > 0 && (
                                      renderActions(strategy.actions)
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/3907/Climate-Action-Resiliency-Plan" target="_blank" rel="noopener noreferrer">
              Climate Action and Resiliency Plan<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default Carp2026;
